import { useEffect, useState } from 'react'
import { apiGet, apiPost } from '../../../generic/Api_Functions'
import { Stack, TextField, Typography } from '@mui/material'
import SmartSelect from '../../../generic/smartSelect/SmartSelect'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { SmartDialog } from '../../../generic/utilities/SmartDialog'
import { FormatListBulleted, PauseCircleOutline } from '@mui/icons-material'
import AlertUI from '../../../generic/AlertUI'
import createDialogTitle from '../../../generic/utilities/dialogUtil'

// Clean toggle for the worker of a presence row: resolves the worker's real state on open
// (getActiveNonProductiveTasks) and offers exactly one action — start XOR stop. The worker
// comes from the row prop: no manual code input and no checkIfExists round-trip.
// Direction (a) of the exclusivity rule (RN3a), reworked in #2784: the offer covers the worker's
// open productive jobs on ALL machines (worker/getOpenProductiveJobs), and accepting it sends ONE
// create with interruptOpenJobs: true — the backend interrupts (or, on machines shared with other
// active workers, closes only this worker's imputation) atomically with the task creation. The
// server guard (S11) remains as the safety net for the flag-less path.
const OtherTaskDialog = ({ open, setOpen, worker }) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert, dismissAlert] = AlertUI()
  // WorkerOtherTaskDto when the worker has an open session; null when the endpoint answered
  // with the "no active tasks" MessageDto (distinguished by the absence of endDate).
  const [activeTask, setActiveTask] = useState(null)
  const [selectableTasks, setSelectableTasks] = useState([])
  const [selectedTask, setSelectedTask] = useState(null)
  const [comment, setComment] = useState('')
  // #2784: open productive jobs of the worker across all machines — non-empty while the
  // "interrupt and start" offer is on screen.
  const [openJobsOffer, setOpenJobsOffer] = useState([])

  useEffect(() => {
    if (!open || !worker) {
      return
    }
    const loadWorkerState = async () => {
      const response = await apiGet('worker/getActiveNonProductiveTasks/' + worker.code)
      if (response && response.endDate === null) {
        setActiveTask(response)
        return
      }
      setActiveTask(null)
      setSelectableTasks(await apiGet('otherTasks/getNonProductiveTasks'))
    }
    loadWorkerState()
      .catch((error) => showAlert({ title: '', message: t_(error.message), severity: 'error' }))
  }, [open, worker, showAlert, t_])

  const resetDialog = () => {
    setOpen(false)
    setActiveTask(null)
    setSelectedTask(null)
    setComment('')
    setOpenJobsOffer([])
    dismissAlert()
  }

  const startTask = async (interruptOpenJobs = false) => {
    await apiPost('mos/create/nonProductiveTask', {
      workerCode: worker.code,
      otherTask: selectedTask,
      comment,
      interruptOpenJobs
    })
    resetDialog()
  }

  const onAccept = async () => {
    try {
      if (activeTask) {
        await apiPost('mos/setEnd/nonProductiveTask/' + activeTask.id)
        resetDialog()
        return
      }
      if (!selectedTask) {
        showAlert({ severity: 'error', title: t_('Please select a task') })
        return
      }
      const openJobs = await apiGet('worker/getOpenProductiveJobs/' + worker.code) || []
      if (openJobs.length > 0) {
        setOpenJobsOffer(openJobs)
        return
      }
      await startTask()
    } catch (error) {
      showAlert({ title: '', message: t_(error.message), severity: 'error' })
    }
  }

  const acceptJobOffer = async () => {
    setOpenJobsOffer([])
    try {
      await startTask(true)
    } catch (error) {
      showAlert({ title: '', message: t_(error.message), severity: 'error' })
    }
  }

  return (
    <>
      <SmartDialog
        title={{
          icon: <FormatListBulleted />,
          render: (<Typography variant='h4'>{t_('Non-productive Task Registration')}</Typography>)
        }}
        setOpen={open}
        acceptCallback={() => onAccept()}
        cancelCallback={() => resetDialog()}
        renderComponent={
          <Stack spacing={2} sx={{ width: '100%', marginTop: '1rem' }}>
            {AlertElement}
            {activeTask
              ? (
                <Typography>
                  {t_('This non productive task is active:')}{' '}
                  <Typography component='span' sx={{ fontWeight: 700, color: '#eab308' }}>
                    {activeTask.otherTask?.name || activeTask.otherTask?.code || ''}
                  </Typography>
                </Typography>
                )
              : (
                <>
                  <SmartSelect
                    label={t_('Choose a non-productive task to register:')}
                    value={selectedTask?.code || null}
                    selectableOptions={selectableTasks.map((task) => task.code)}
                    onChange={(newValue) => {
                      setSelectedTask(selectableTasks.find((task) => task.code === newValue) ?? null)
                    }}
                  />
                  <TextField
                    label={t_('Comment')}
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    fullWidth
                  />
                </>
                )}
          </Stack>
        }
      />
      {openJobsOffer.length > 0 && (
        <SmartDialog
          title={createDialogTitle({ color: 'warning', title: t_('Open productive job'), icon: PauseCircleOutline })}
          setOpen
          acceptLabel='Interrupt and start'
          acceptCallback={() => acceptJobOffer()}
          cancelCallback={() => setOpenJobsOffer([])}
          renderComponent={
            <Typography sx={{ padding: '1rem' }}>
              {t_('The worker has an open productive job:')}{' '}
              <Typography component='span' sx={{ fontWeight: 700 }}>
                {openJobsOffer.map((job) => `${job.fabricationOrderCode} (${job.deviceName})`).join(', ')}
              </Typography>
            </Typography>
          }
        />
      )}
    </>
  )
}

export default OtherTaskDialog
